import { ReactNode } from 'react';

export const SortDirectionEnum = {
  ASC: 'asc',
  DESC: 'desc'
} as const;

export type TSortDirection = (typeof SortDirectionEnum)[keyof typeof SortDirectionEnum];

export type TTableSort = {
  key: string;
  direction: TSortDirection;
};

export type TTableColumn<TRow> = {
  /** Stable identifier — also used as the sort key when the column is sortable. */
  key: string;
  header: ReactNode;
  canSort?: boolean;
  /** Hides the column on tablet and mobile breakpoints. */
  hideOnSmallScreens?: boolean;
  thClassName?: string;
  tdClassName?: string;
  /**
   * Returns a comparable value for client-side sorting on this column. `null` means "no value
   * to sort by" - that row always sorts after every row with a real value, in EITHER sort
   * direction (see DataTable's comparator), rather than being treated as +/-Infinity, which
   * would otherwise flip which end it lands on when the sort direction is reversed.
   */
  sortAccessor?: (row: TRow) => string | number | null;
  /** Cell renderer. Ignored when `renderRow` is supplied to the table. */
  cell?: (row: TRow, index: number) => ReactNode;
  /**
   * Fixed column width (e.g. '120px', '30%') - only meaningful together with `maxBodyHeight`,
   * where the header and body render as two separate `<table>`s (so the header can stay outside
   * the scrolling area) that need matching `table-layout: fixed` column widths to stay aligned.
   * Columns left unset split the remaining width evenly between them - normal `table-layout:
   * fixed` behaviour, no measurement needed as long as both tables render at the same overall
   * width. Ignored entirely outside `maxBodyHeight` mode.
   */
  width?: string;
};

/**
 * Pagination behaviour.
 * - `server`: parent owns page state, supplies the current page slice and total count.
 * - `client`: the table slices `data` itself and tracks the active page internally.
 * - `none`: render every row, no footer pagination.
 */
export type TTablePagination =
  | {
      mode: 'server';
      currentPage: number;
      pageSize: number;
      totalItemsCount: number;
      onPageChange: (page: number) => void;
    }
  | { mode: 'client'; pageSize: number }
  | { mode: 'none' };

/**
 * Sorting behaviour.
 * - `server`: parent owns the sort and refetches; the table only emits changes.
 * - `client`: the table sorts `data` itself using each column's `sortAccessor`.
 * - `none`: no sortable columns.
 */
export type TTableSorting =
  | { mode: 'server'; sort?: TTableSort; onSortChange: (sort: TTableSort) => void }
  | { mode: 'client'; initialSort?: TTableSort }
  | { mode: 'none' };

/**
 * Row-selection behaviour. Adds a leading checkbox column to the header and
 * (for the default row renderer) each row. When `renderRow` is supplied,
 * the caller is responsible for rendering its own per-row checkbox using
 * this same state — the table still renders the header "select all" checkbox.
 */
export type TTableSelection<TRow> = {
  selectedKeys: Set<string | number>;
  onToggleRow: (row: TRow, index: number) => void;
  onToggleAll: () => void;
  /**
   * 0-based position of the checkbox column among `columns` - e.g. 1 inserts it after the first
   * column instead of before it. Defaults to 0 (leftmost), unchanged from before this existed.
   * Clamped to the valid range by DataTable, so an out-of-bounds value is simply pinned to
   * whichever end it overshot rather than throwing.
   */
  columnPosition?: number;
};
